import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { SourcingService, SourcingSetting } from 'api-clients/monolith';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import { updateSourcingSettings } from '@/api-clients/update-sourcing-settings';
import { useToaster } from '@/components/base/toaster';
import { Typography } from '@/components/ui/typography';

import { AutomationSetting } from './automation-setting';

const messages = defineMessages({
  funnelAutomationTitle: {
    defaultMessage: 'Funnel Automations',
    description: 'Title for funnel automations setting',
  },
  funnelAutomationDescription: {
    defaultMessage:
      'Automatically pauses sourcing when the opening is on track to hit the hiring goal, based on performance across key workflow stages.',
    description: 'Description of funnel automations',
  },
  hiringAutomationTitle: {
    defaultMessage: 'Hiring Automations',
    description: 'Title for hiring automations setting',
  },
  hiringAutomationDescription: {
    defaultMessage:
      'Automatically pauses sourcing when the hiring goal period ends, so you do not keep spending after the window closes.',
    description: 'Description of hiring automations',
  },
  budgetAutomationTitle: {
    defaultMessage: 'Budget Automations',
    description: 'Title for budget automations setting',
  },
  budgetAutomationDescription: {
    defaultMessage:
      'Keeps spend on track with budget alerts and automatic pauses when you reach your target budget.',
    description: 'Description of budget automations',
  },
});

const automationSettings: Array<{
  key: keyof SourcingSetting;
  titleMessage: keyof typeof messages;
  descriptionMessage: keyof typeof messages;
}> = [
  {
    key: 'funnel_automation_enabled',
    titleMessage: 'funnelAutomationTitle',
    descriptionMessage: 'funnelAutomationDescription',
  },
  {
    key: 'hiring_automation_enabled',
    titleMessage: 'hiringAutomationTitle',
    descriptionMessage: 'hiringAutomationDescription',
  },
  {
    key: 'budget_automation_enabled',
    titleMessage: 'budgetAutomationTitle',
    descriptionMessage: 'budgetAutomationDescription',
  },
];

interface SettingsPageContentProps {
  settings?: SourcingSetting;
  onSettingChange?: (key: keyof SourcingSetting, checked: boolean) => void;
  disabled?: boolean;
}

function SettingsPageContent({
  settings,
  onSettingChange,
  disabled = false,
}: SettingsPageContentProps) {
  return (
    <div className="rounded-lg bg-white p-6 shadow-sm">
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <Typography variant="h5" as="h2">
            <FormattedMessage
              defaultMessage="Enable Automations for All Openings"
              description="Main section title for enabling automations"
            />
          </Typography>
          <Typography variant="body1" className="!text-fx-neutral-600">
            <FormattedMessage
              defaultMessage="Automations help cut wasted spend by pausing sourcing based on hiring goal or budget targets. Review what each automation does below before enabling."
              description="Description explaining how automations help cut wasted spend"
            />
          </Typography>
        </div>

        <div className="flex flex-col gap-6">
          {automationSettings.map(setting => (
            <AutomationSetting
              key={setting.key}
              title={<FormattedMessage {...messages[setting.titleMessage]} />}
              description={
                <FormattedMessage {...messages[setting.descriptionMessage]} />
              }
              checked={settings?.[setting.key] ?? false}
              onCheckedChange={checked =>
                onSettingChange?.(setting.key, checked)
              }
              disabled={disabled}
              isLoading={!settings}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export function SettingsPage() {
  const intl = useIntl();
  const { notifySuccess, notifyError } = useToaster();
  const queryClient = useQueryClient();

  const { data } = useSuspenseQuery<SourcingSetting>({
    queryKey: ['sourcingSettings'] as const,
    queryFn: () =>
      SourcingService.getInternalApiSourcingSourcingSettings() as Promise<SourcingSetting>,
  });

  const settings: SourcingSetting = data;

  const { mutate: updateSettings, isPending } = useMutation({
    mutationFn: updateSourcingSettings,
    onMutate: variables => {
      const previousSettings = queryClient.getQueryData<SourcingSetting>([
        'sourcingSettings',
      ]);
      const optimisticSettings: SourcingSetting = {
        ...settings,
        ...variables,
      };
      queryClient.setQueryData<SourcingSetting>(
        ['sourcingSettings'],
        optimisticSettings,
      );
      return { previousSettings };
    },
    onSuccess: response => {
      queryClient.setQueryData<SourcingSetting>(['sourcingSettings'], response);
      notifySuccess({
        message: intl.formatMessage({
          defaultMessage: 'Settings updated successfully!',
          description: 'Success message for updating sourcing settings',
        }),
      });
    },
    onError: (_error, _variables, context) => {
      if (context?.previousSettings) {
        const previousSettings = context.previousSettings as SourcingSetting;
        queryClient.setQueryData<SourcingSetting>(
          ['sourcingSettings'],
          previousSettings,
        );
      }
      notifyError({
        message: intl.formatMessage({
          defaultMessage: 'Failed to update settings!',
          description: 'Error message for updating sourcing settings',
        }),
      });
    },
  });

  const handleSettingChange = (
    key: keyof SourcingSetting,
    checked: boolean,
  ) => {
    updateSettings({
      [key]: checked,
    });
  };

  return (
    <SettingsPageContent
      settings={settings}
      onSettingChange={handleSettingChange}
      disabled={isPending}
    />
  );
}

SettingsPage.Skeleton = function SettingsPageSkeleton() {
  return <SettingsPageContent />;
};
